import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import type { V2AccountDataType } from '@/features/auth/types'
import { getConversations, V1_CONVERSATIONS_QUERY_KEY } from '@/features/chat/api'
import { useTemporaryConversations } from '@/features/chat/composables/use-temporary-conversations.composable'
import type { V1ConversationType, V2ConversationType } from '@/features/chat/types'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'

function V1ToV2DataMiddleware({
  data,
  accountRef,
}: {
  data: V1ConversationType[] | undefined
  accountRef: Partial<V2AccountDataType> | undefined
}): V2ConversationType[] {
  const conversations = data?.map((conversation) => {
    const isGroupConversation = !!conversation.group
    const groupName = isGroupConversation ? (conversation.group?.customName ?? conversation.group?.name) : null
    const correspondent = conversation.users.find((user) => user.id !== accountRef?.id)
    const correspondentName = correspondent?.firstname + ' ' + correspondent?.lastname

    return {
      conversationId: conversation.conversationId,
      groupId: conversation.group?.id,
      groupName: groupName ?? correspondentName,
      conversationName: isGroupConversation
        ? // concat all firstName
          groupName
        : // take user full name
          correspondentName,
      messages:
        conversation.messages?.map((message) => ({
          ...message,
          sentAt: message.sendAt,
        })) || [],
      users:
        conversation?.users?.map((user) => ({
          id: user.id,
          roles: user.roles,
          firstName: user.firstname,
          lastName: user.lastname,
          picture: user.profilPicture ?? null,
          hasSeen: conversation.views.includes(user.id),
        })) || [],
      unreadCount: conversation.unreadCount ?? 0,
      programId: conversation.programId,
    }
  })

  return conversations || []
}

function useConversations() {
  const { isAuthRef } = useIsAuth()
  const { accountRef } = useAccount()
  const currentRouteRef = selectGetCurrentRoute(routeStore)
  const { allTemporaryConversationsRef, isTemporaryConversation } = useTemporaryConversations()
  const selectedConversationIdRef = computed(() => {
    const roomId = currentRouteRef.value?.params?.roomId as string | undefined
    return roomId ?? undefined
  })

  const conversationIdForApiRef = computed(() => {
    const id = selectedConversationIdRef.value
    if (id && isTemporaryConversation(id)) {
      return undefined
    }
    return id
  })

  const isGetConversationsEnabledRef = computed(
    () =>
      !!isAuthRef.value &&
      !(selectedConversationIdRef.value && isTemporaryConversation(selectedConversationIdRef.value)),
  )

  const { data, isPending } = useQuery({
    queryKey: [V1_CONVERSATIONS_QUERY_KEY, { conversationId: conversationIdForApiRef.value }],
    queryFn: () =>
      // page de chat tous programmes : pas de filtre par programme
      getConversations({
        conversationId: conversationIdForApiRef.value,
      }),
    enabled: isGetConversationsEnabledRef,
    refetchOnMount: true,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  const realConversationsRef = computed(() => V1ToV2DataMiddleware({ data: data.value, accountRef: accountRef.value }))
  const conversationsRef = computed(() => {
    const real = realConversationsRef.value
    const temp = allTemporaryConversationsRef.value
    const tempFiltered = temp.filter((tempConv) => {
      return !real.some((realConv) => {
        if (!realConv.groupId && !tempConv.groupId) {
          const realUserIds = realConv.users.map((u) => u.id).sort()
          const tempUserIds = tempConv.users.map((u) => u.id).sort()
          return JSON.stringify(realUserIds) === JSON.stringify(tempUserIds)
        }
        return false
      })
    })

    return [...real, ...tempFiltered]
  })

  return {
    conversationsRef,
    isPendingRef: isPending,
  }
}

export default useConversations
